import React, { useState, useEffect } from 'react';
import { Form, Table, Alert, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import RendicontazioneAdminService from '../../services/RendicontazioneAdminService/RendicontazioneAdminService';

const ANNO_INIZIO = 2025;

/**
 * Calcola l'importo netto della liquidazione applicando gli scaglioni configurati
 * per il corso. Ogni scaglione ha un importo_netto (= importo_lordo - sconto_fonte).
 */
function calcolaRendicontazione(totaleCorsi, scaglioni) {
    if (!totaleCorsi || totaleCorsi <= 0 || !Array.isArray(scaglioni)) return 0;
    let importo = 0;
    scaglioni.forEach((sc) => {
        const da = parseInt(sc.da_quantita, 10);
        const a = sc.a_quantita == null ? null : parseInt(sc.a_quantita, 10);
        if (totaleCorsi < da) return;
        const limiteAlto = a == null ? totaleCorsi : Math.min(totaleCorsi, a);
        const quantita = limiteAlto - da + 1;
        if (quantita > 0) importo += quantita * (parseFloat(sc.importo_netto) || 0);
    });
    return importo;
}

function formatScaglione(sc) {
    const da = parseInt(sc.da_quantita, 10);
    const a = sc.a_quantita == null ? null : parseInt(sc.a_quantita, 10);
    const range = a == null ? `Oltre i ${da - 1} corsi` : `Da ${da} corsi a ${a} corsi`;
    return `${range}: € ${(parseFloat(sc.importo_lordo) || 0).toFixed(2)} cad. − € ${(parseFloat(sc.importo_lordo) - parseFloat(sc.importo_netto) || 0).toFixed(2)} sconto = € ${(parseFloat(sc.importo_netto) || 0).toFixed(2)} cad.`;
}

export default function LiquidazioneAnnualePage() {
    const user = useSelector((state) => state.user.userData);
    const { idCorso } = useParams();
    const currentYear = new Date().getFullYear();
    const [anno, setAnno] = useState(currentYear);
    const [listaPartner, setListaPartner] = useState([]);
    const [corso, setCorso] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const idSuperpartner = user?.id_superpartner;
    const nomeSuperpartner = user?.nome || user?.username || '';

    const anniOptions = [];
    for (let y = ANNO_INIZIO; y <= currentYear; y++) {
        anniOptions.push(y);
    }

    useEffect(() => {
        if (!idSuperpartner || !idCorso) {
            setCorso(null);
            return;
        }
        let cancelled = false;
        RendicontazioneAdminService.getCorsiLiquidazione(idSuperpartner)
            .then((data) => {
                if (cancelled) return;
                const found = (Array.isArray(data) ? data : []).find((c) => String(c.id_corso) === String(idCorso));
                setCorso(found || null);
            })
            .catch(() => { if (!cancelled) setCorso(null); });
        return () => { cancelled = true; };
    }, [idSuperpartner, idCorso]);

    useEffect(() => {
        if (!idSuperpartner || !anno || !idCorso) {
            setListaPartner([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        RendicontazioneAdminService.getLiquidazioneAnnualeSuperpartner(idSuperpartner, anno, idCorso)
            .then((data) => {
                if (!cancelled) {
                    setListaPartner(Array.isArray(data) ? data : []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Errore nel caricamento della liquidazione annuale.');
                    setListaPartner([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [idSuperpartner, anno, idCorso]);

    if (idSuperpartner == null || idSuperpartner === '') {
        return (
            <div className="p-4">
                <h5 className="mb-3">Liquidazione annuale</h5>
                <Alert variant="warning">Non sei associato a un superpartner. Impossibile visualizzare la liquidazione annuale.</Alert>
            </div>
        );
    }
    if (idSuperpartner != 2) {
        return (
            <div className="p-4">
                <h5 className="mb-3">Liquidazione annuale</h5>
                <Alert variant="info">La liquidazione annuale è disponibile solo per il superpartner autorizzato.</Alert>
            </div>
        );
    }

    const scaglioni = corso?.scaglioni || [];
    const totaleCorsi = listaPartner.reduce((sum, p) => sum + (parseInt(p.num_corsi_attivati, 10) || 0), 0);
    const importoRendicontazione = calcolaRendicontazione(totaleCorsi, scaglioni);
    const prezzoContratto = parseFloat(corso?.prezzo_contratto) || 0;
    const scontoFonte = parseFloat(corso?.sconto_fonte) || 0;

    return (
        <div className="p-4">
            <h5 className="mb-3">Liquidazione annuale – {nomeSuperpartner}</h5>
            <h6 className="mb-3 text-muted">{corso?.etichetta || 'Corso'}</h6>

            <Form.Group className="mb-3">
                <Form.Label>Anno</Form.Label>
                <Form.Select
                    value={anno}
                    onChange={(e) => setAnno(parseInt(e.target.value, 10))}
                    disabled={loading}
                >
                    {anniOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            {error && <Alert variant="danger">{error}</Alert>}
            {!corso && !loading && !error && (
                <Alert variant="warning">Corso non configurato per la liquidazione annuale.</Alert>
            )}

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Caricamento...
                </div>
            ) : (
                <>
                    <Table striped bordered hover size="sm" className="mb-4">
                        <thead className="table-light">
                            <tr>
                                <th>Partner associati</th>
                                <th className="text-end" style={{ width: '180px' }}>Corsi Attivati (anno {anno})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listaPartner.length === 0 ? (
                                <tr>
                                    <td colSpan={2} className="text-center text-muted">Nessun partner o nessun dato per l'anno selezionato.</td>
                                </tr>
                            ) : (
                                listaPartner.map((row) => (
                                    <tr key={row.id_agenzia}>
                                        <td>{row.denominazione || '-'}</td>
                                        <td className="text-end">{row.num_corsi_attivati ?? 0}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    <div className="border rounded p-3 bg-light">
                        <h6 className="mb-2">Rendicontazione ({corso?.etichetta || 'corso'})</h6>
                        {prezzoContratto > 0 && (
                            <p className="small text-muted mb-2">
                                Prezzo contrattuale del corso: <strong>€ {prezzoContratto.toFixed(2)}</strong>.
                                {scontoFonte > 0 && (
                                    <> Sconto alla fonte applicato: <strong>€ {scontoFonte.toFixed(2)} a corso</strong>. Dagli importi per scaglione va detratto lo sconto per ottenere l'importo netto liquidabile.</>
                                )}
                            </p>
                        )}
                        <p className="small text-muted mb-2">
                            Totale corsi attivati nell'anno: <strong>{totaleCorsi}</strong>
                        </p>
                        <p className="small mb-1"><strong>Scaglioni (lordo − sconto = netto):</strong></p>
                        <ul className="small mb-1">
                            {scaglioni.map((sc, i) => (
                                <li key={i}>{formatScaglione(sc)}</li>
                            ))}
                        </ul>
                        <p className="mb-0 mt-2">
                            <strong>Importo liquidazione annuale: € {importoRendicontazione.toFixed(2)}</strong>
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}
